import { ColDef, ColGroupDef, ColumnState } from "ag-grid-community";
import { EQM2040_R01Dto } from "../Dto_EQM2040_R01";
import { getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { aggridHeaderClass } from "src/components/common/common";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { formatGridNumber2 } from "src/utils/number/numberUtils";
import { formatDate } from "src/utils/string/stringUtil";
import {
	getDayOfWeekIndexFromDateString,
	getDayNameFromDateString,
} from "src/utils/date/dateUtils";

/**
 * 헤더 이름 매핑
 */
export const headerNames: Record<string, string> = {
	rowIndex: "",
	inspectionDt: "일자",
	dayOfWeek: "요일",
	industrialWater: '산업용수',
	domesticWater: '생활용수',
	electricalEnergy01: '옥상1',
	electricalEnergy02: '옥상2',
	electricalEnergy03: '옥상3',
	remark: '비고'
};

/**
 * 기본 컬럼 정의 생성 함수
 */
export const createBaseColumnDefs = (
	cellDateComparator: (valueA: Date, valueB: Date) => number
): (ColDef<EQM2040_R01Dto> | ColGroupDef<EQM2040_R01Dto>)[] => {
	return [
		// ...getDefaultRowIndexColumnConfig(renderIndexCell),
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '일자',
			colId: 'inspectionDt',
			field: 'inspectionDt',
			width: 100,
			valueFormatter: (params: any) => {
				if (!params.data?.inspectionDt) return '';
				return formatDate(new Date(params.data.inspectionDt));
			},
			comparator: cellDateComparator,
			editable: false,
		},
		{
			headerName: '요일',
			colId: 'dayOfWeek',
			width: 50,
			valueGetter: (params: any) => getDayNameFromDateString(params.data?.inspectionDt),
			cellClassRules: {
				'eqm2050-sunday-cell': (params: any) => getDayOfWeekIndexFromDateString(params.data?.inspectionDt) === 0,
				'eqm2050-saturday-cell': (params: any) => getDayOfWeekIndexFromDateString(params.data?.inspectionDt) === 6,
				'text-align-center': (params: any) => true,
			},
			editable: false,
		},
		{
			headerName: '실험실',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: '온도(℃)',
					colId: 'laboratory01',
					field: 'laboratory01',
					width: 100,
					valueFormatter: formatGridNumber2,
					editable: true,
				},
				{
					headerName: '습도(%)',
					colId: 'laboratory02',
					field: 'laboratory02',
					width: 100,
					valueFormatter: formatGridNumber2,
					editable: true,
				},
			],
		},
		{
			headerName: '약품실',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: '온도(℃)',
					colId: 'pharmacy01',
					field: 'pharmacy01',
					width: 100,
					valueFormatter: formatGridNumber2,
					editable: true,
				},
				{
					headerName: '습도(%)',
					colId: 'pharmacy02',
					field: 'pharmacy02',
					width: 100,
					valueFormatter: formatGridNumber2,
					editable: true,
				},
			],
		},
		{
			headerName: 'X-RAY 측정실',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: '온도(℃)',
					colId: 'xrayRoom01',
					field: 'xrayRoom01',
					width: 100,
					valueFormatter: formatGridNumber2,
					editable: true,
				},
				{
					headerName: '습도(%)',
					colId: 'xrayRoom02',
					field: 'xrayRoom02',
					width: 100,
					valueFormatter: formatGridNumber2,
					editable: true,
				},
			],
		},
		{
			headerName: '비고',
			colId: 'remark',
			field: 'remark',
			width: 200,
			editable: true,
		},
	];
};

/**
 * 그룹 헤더 구조를 유지하면서 헤더 클래스 정규화
 */
const normalizeHeaderClass = (
	defs: (ColDef<EQM2040_R01Dto> | ColGroupDef<EQM2040_R01Dto>)[]
): (ColDef<EQM2040_R01Dto> | ColGroupDef<EQM2040_R01Dto>)[] => {
	return defs.map((def: any) => {
		// children이 있으면 재귀적으로 처리
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;

		if (children) {
			// ColGroupDef인 경우 - 공통 그룹 헤더 클래스만 유지
			const groupHeaderClass = [def.headerClass, 'common-group-header']
				.filter(Boolean)
				.join(' ');
			return {
				...def,
				headerClass: groupHeaderClass,
				suppressMenu: true,
				children
			} as ColGroupDef<EQM2040_R01Dto>;
		} else {
			// ColDef인 경우 - 일반 헤더는 aggridHeaderClass 적용
			const mergedHeaderClass = [aggridHeaderClass, def.headerClass]
				.filter(Boolean)
				.join(' ');
			return {
				...def,
				headerClass: mergedHeaderClass,
				suppressMenu: true
			} as ColDef<EQM2040_R01Dto>;
		}
	}).filter(Boolean) as (ColDef<EQM2040_R01Dto> | ColGroupDef<EQM2040_R01Dto>)[];
};

/**
 * 최종 컬럼 정의 생성 (컬럼 상태 병합 포함)
 */
export const createColumnDefs = (
	baseColumnDefs: (ColDef<EQM2040_R01Dto> | ColGroupDef<EQM2040_R01Dto>)[],
	columnState: ColumnState[] | null
): (ColDef<EQM2040_R01Dto> | ColGroupDef<EQM2040_R01Dto>)[] => {
	// analysisDt 컬럼을 제거한 baseColumnDefs 사용
	// analysisDt 컬럼은 이미 baseColumnDefs에 포함되어 있으므로 필터링 불필요
	const filteredBaseColumnDefs = baseColumnDefs;

	const mergedColumnDefs = GridCustomColumnDefs<EQM2040_R01Dto>({
		baseColumnDefs: filteredBaseColumnDefs,
		columnState: columnState || null,
		headerNames,
		fallbackHeaderClass: aggridHeaderClass
	});

	return normalizeHeaderClass(mergedColumnDefs);
};

